import { InteractionProduct } from 'b2b-apps/core/analytics/redux';
import { ProductFullType } from 'b2b-apps/core/product/redux/products/full/types';
import { ProductTileType } from 'b2b-apps/core/product/redux/products/tile/types';
import { TEALIUM_INTERACTION_ID, TEALIUM_PROD_ACTION } from 'b2b-apps/shop/analytics/types/types';
import { calculateProductPosition, preparePrice, prepareStock } from 'b2b-apps/shop/analytics/utils/analytics';
import { ProductType } from 'b2b-apps/shop/catalog/redux/search/types';
import { UseProductsTilesReturnType } from 'b2b-apps/shop/product/hooks';
import { ProductAccessoryType } from 'b2b-apps/shop/product/redux/productAccessories/types';
import { DeliveredOrderProductType } from 'b2b-common/core/account/api/DeliveredOrders.types';
import { WishlistItemType } from 'b2b-common/core/account/api/wishlists';
import { CartProductType } from 'b2b-common/core/cart/api/CartTypes';
import { ComparedProductDetails } from 'b2b-common/core/product/api/ProductsCompare.types';

interface ProductsListAnalyticsEventData {
  data: UseProductsTilesReturnType | ProductAccessoryType[];
  skuList: ProductType[];
  page: number;
  pageSize: number;
  pageType?: string;
  pageViewPageType?: string;
}

interface ProductsListAnalyticsReturnType {
  interaction: string;
  prod: Array<Record<string, string>>;
}

const createProductsListsAnalyticsEventData = (
  analyticsData: ProductsListAnalyticsEventData,
): ProductsListAnalyticsReturnType => {
  const { data, skuList, pageType = '', page, pageSize } = analyticsData;
  const productCollection = [
    ...Object.values(data).map(product => ({
      ...(product?.data ? product?.data : product),
      order: skuList?.findIndex(
        productIndex => productIndex?.sku === product?.data?.sku || productIndex?.sku === product?.sku,
      ),
    })),
  ].filter(product => product?.order !== -1);

  return {
    interaction: TEALIUM_INTERACTION_ID.productList,
    prod: productCollection.map((product) => ({
      action: TEALIUM_PROD_ACTION.productsList,
      categories: product.analytics ? product.analytics.categories : [product.category?.label],
      id: product.sku.toString(),
      pos: calculateProductPosition(product.order, pageSize, page),
      price: preparePrice(product.effectivePricing.userPriceWithoutVat),
      stock: prepareStock(product.settings.quantity),
      list: pageType,
    })),
  };
};

export const getInteractionProduct = ({
  sku,
  effectivePricing,
  settings,
  analytics,
}: ProductFullType | ProductTileType | ProductAccessoryType): InteractionProduct => ({
  sku: sku,
  price: effectivePricing.userPriceWithoutVat,
  isPurchasable: settings.isBuyable && !settings.isDisabledByCoreAssortment,
  categories: analytics.categories,
  stock: settings.quantity,
});

export const getInteractionProductFromDeliveredOrderItem = ({
  sku,
  settings,
  category,
  priceValueWithoutVat,
}: DeliveredOrderProductType): InteractionProduct => ({
  sku: sku,
  price: priceValueWithoutVat,
  isPurchasable: settings.isBuyable && !settings.isDisabledByCoreAssortment,
  categories: [category.label],
  stock: settings.quantity,
});

export const getInteractionProductFromCartItem = ({
  sku,
  single,
  settings,
  analytics,
}: CartProductType | WishlistItemType): InteractionProduct => ({
  sku: sku,
  price: single.priceWithoutVat,
  isPurchasable: settings.isBuyable && !settings.isDisabledByCoreAssortment,
  categories: analytics.categories,
  stock: settings.quantity,
});

export const getInteractionProductFromComparison = ({
  sku,
  pricing,
  settings,
  analytics,
}: ComparedProductDetails): InteractionProduct => ({
  sku: sku,
  price: pricing.userPriceWithoutVat,
  isPurchasable: settings.isBuyable && !settings.isDisabledByCoreAssortment,
  categories: analytics.categories,
  stock: settings.quantity,
});

export { createProductsListsAnalyticsEventData };
